import {isLexicalJsonString, toPlainTextLexicalJson} from 'components/DSYS/editor/utils/lexical';

type MutableLexicalNode = {
  type?: string;
  text?: string;
  children?: MutableLexicalNode[];
  [key: string]: unknown;
};

type MutableLexicalJson = {
  root?: MutableLexicalNode;
  [key: string]: unknown;
};

type TextSpan = {
  node: MutableLexicalNode;
  start: number;
  end: number;
};

type PlainTextModel = {
  text: string;
  textSpans: TextSpan[];
};

type LcsPair = {
  previousIndex: number;
  nextIndex: number;
};

const MAX_LCS_MATRIX_CELLS = 1_500_000;

export const getWorkstreamDescriptionPlainText = (description?: string | null) => {
  if (!description) {
    return '';
  }

  if (!isLexicalJsonString(description)) {
    return description;
  }

  try {
    const parsed = JSON.parse(description) as MutableLexicalJson;

    if (!parsed.root) {
      return description;
    }

    return buildPlainTextModel(parsed.root).text;
  } catch {
    return description;
  }
};

export const hasMeaningfulWorkstreamDescriptionContent = (description?: string | null) =>
  getWorkstreamDescriptionPlainText(description).trim().length > 0;

export const getWorkstreamDescriptionEditorValue = (description?: string | null) => {
  if (!description) {
    return '';
  }

  return isLexicalJsonString(description) ? description : toPlainTextLexicalJson(description);
};

const isParagraphLikeNode = (node: MutableLexicalNode) =>
  node.type === 'paragraph' || node.type === 'listitem';

const buildPlainTextModel = (root: MutableLexicalNode): PlainTextModel => {
  const textSpans: TextSpan[] = [];
  let text = '';

  const appendText = (node: MutableLexicalNode, nodeText: string) => {
    const start = text.length;
    text += nodeText;
    textSpans.push({
      node,
      start,
      end: text.length,
    });
  };

  const visit = (node: MutableLexicalNode) => {
    if (node.type === 'text') {
      appendText(node, node.text ?? '');
      return;
    }

    node.children?.forEach(visit);

    if (isParagraphLikeNode(node)) {
      text += '\n';
    }
  };

  visit(root);

  if (text.endsWith('\n')) {
    text = text.slice(0, -1);
  }

  return {
    text,
    textSpans,
  };
};

const getSingleChangedRange = (previousText: string, nextText: string) => {
  let start = 0;

  while (
    start < previousText.length &&
    start < nextText.length &&
    previousText[start] === nextText[start]
  ) {
    start += 1;
  }

  let previousEnd = previousText.length;
  let nextEnd = nextText.length;

  while (
    previousEnd > start &&
    nextEnd > start &&
    previousText[previousEnd - 1] === nextText[nextEnd - 1]
  ) {
    previousEnd -= 1;
    nextEnd -= 1;
  }

  return {
    start,
    previousEnd,
    replacementText: nextText.slice(start, nextEnd),
  };
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const findInsertionSpan = (textSpans: TextSpan[], position: number) => {
  return (
    textSpans.find((span) => span.start <= position && position <= span.end) ??
    [...textSpans].reverse().find((span) => span.start <= position) ??
    textSpans[0]
  );
};

const getLcsPairs = (previousText: string, nextText: string): LcsPair[] | null => {
  const matrixCells = (previousText.length + 1) * (nextText.length + 1);

  if (matrixCells > MAX_LCS_MATRIX_CELLS) {
    return null;
  }

  const lengths = Array.from(
    {length: previousText.length + 1},
    () => new Uint32Array(nextText.length + 1)
  );

  for (let previousIndex = previousText.length - 1; previousIndex >= 0; previousIndex -= 1) {
    for (let nextIndex = nextText.length - 1; nextIndex >= 0; nextIndex -= 1) {
      lengths[previousIndex][nextIndex] =
        previousText[previousIndex] === nextText[nextIndex]
          ? lengths[previousIndex + 1][nextIndex + 1] + 1
          : Math.max(lengths[previousIndex + 1][nextIndex], lengths[previousIndex][nextIndex + 1]);
    }
  }

  const pairs: LcsPair[] = [];
  let previousIndex = 0;
  let nextIndex = 0;

  while (previousIndex < previousText.length && nextIndex < nextText.length) {
    if (previousText[previousIndex] === nextText[nextIndex]) {
      pairs.push({previousIndex, nextIndex});
      previousIndex += 1;
      nextIndex += 1;
    } else if (lengths[previousIndex + 1][nextIndex] >= lengths[previousIndex][nextIndex + 1]) {
      previousIndex += 1;
    } else {
      nextIndex += 1;
    }
  }

  return pairs;
};

const buildPreviousIndexSpanMap = (textSpans: TextSpan[], previousTextLength: number) => {
  const previousIndexSpanMap = new Array<TextSpan | undefined>(previousTextLength);

  for (const textSpan of textSpans) {
    for (let index = textSpan.start; index < textSpan.end; index += 1) {
      previousIndexSpanMap[index] = textSpan;
    }
  }

  return previousIndexSpanMap;
};

const findFirstSpanAfterPosition = (textSpans: TextSpan[], position: number) => {
  return textSpans.find((textSpan) => textSpan.start > position);
};

const applyAlignedPlainTextToTextSpans = (
  textSpans: TextSpan[],
  previousText: string,
  nextText: string
) => {
  const lcsPairs = getLcsPairs(previousText, nextText);

  if (!lcsPairs) {
    return false;
  }

  const spanIndexBySpan = new Map<TextSpan, number>();
  textSpans.forEach((textSpan, index) => {
    spanIndexBySpan.set(textSpan, index);
  });

  const previousIndexSpanMap = buildPreviousIndexSpanMap(textSpans, previousText.length);
  const assignedTextBySpan = textSpans.map(() => '');
  let lastAssignedSpan: TextSpan | undefined;
  let spanAfterBoundary: TextSpan | undefined;
  let nextCursor = 0;

  const appendToSpan = (textSpan: TextSpan | undefined, text: string) => {
    if (!textSpan || !text) {
      return;
    }

    const spanIndex = spanIndexBySpan.get(textSpan);
    if (spanIndex === undefined) {
      return;
    }

    assignedTextBySpan[spanIndex] += text;
    lastAssignedSpan = textSpan;
  };

  for (const pair of lcsPairs) {
    const pairSpan = previousIndexSpanMap[pair.previousIndex];
    const insertionText = nextText.slice(nextCursor, pair.nextIndex);
    appendToSpan(lastAssignedSpan ?? pairSpan ?? spanAfterBoundary ?? textSpans[0], insertionText);

    if (!pairSpan) {
      lastAssignedSpan = undefined;
      spanAfterBoundary = findFirstSpanAfterPosition(textSpans, pair.previousIndex);
      nextCursor = pair.nextIndex + 1;
      continue;
    }

    spanAfterBoundary = undefined;
    appendToSpan(pairSpan, nextText[pair.nextIndex]);
    nextCursor = pair.nextIndex + 1;
  }

  appendToSpan(lastAssignedSpan ?? spanAfterBoundary ?? textSpans[0], nextText.slice(nextCursor));

  for (let index = 0; index < textSpans.length; index += 1) {
    const textSpan = textSpans[index];
    textSpan.node.text = assignedTextBySpan[index];
  }

  return true;
};

export const mergePlainTextIntoRichDescription = (
  previousDescription: string,
  nextPlainText: string
) => {
  if (!nextPlainText) {
    return '';
  }

  if (!isLexicalJsonString(previousDescription)) {
    return toPlainTextLexicalJson(nextPlainText);
  }

  const previousPlainText = getWorkstreamDescriptionPlainText(previousDescription);

  if (previousPlainText === nextPlainText) {
    return previousDescription;
  }

  try {
    const parsed = JSON.parse(previousDescription) as MutableLexicalJson;

    if (!parsed.root) {
      return toPlainTextLexicalJson(nextPlainText);
    }

    const {text: previousText, textSpans} = buildPlainTextModel(parsed.root);
    const populatedTextSpans = textSpans.filter((span) => span.end > span.start);

    if (!populatedTextSpans.length) {
      return toPlainTextLexicalJson(nextPlainText);
    }

    if (applyAlignedPlainTextToTextSpans(populatedTextSpans, previousText, nextPlainText)) {
      return JSON.stringify(parsed);
    }

    const {start, previousEnd, replacementText} = getSingleChangedRange(
      previousText,
      nextPlainText
    );

    if (start === previousEnd) {
      const span = findInsertionSpan(populatedTextSpans, start);
      const spanText = span.node.text ?? '';
      const offset = clamp(start - span.start, 0, spanText.length);

      span.node.text = `${spanText.slice(0, offset)}${replacementText}${spanText.slice(offset)}`;
      return JSON.stringify(parsed);
    }

    const changedSpans = populatedTextSpans.filter(
      (span) => span.end > start && span.start < previousEnd
    );

    if (!changedSpans.length) {
      const span = findInsertionSpan(populatedTextSpans, start);
      const spanText = span.node.text ?? '';
      const offset = clamp(start - span.start, 0, spanText.length);

      span.node.text = `${spanText.slice(0, offset)}${replacementText}${spanText.slice(offset)}`;
      return JSON.stringify(parsed);
    }

    const firstSpan = changedSpans[0];
    const lastSpan = changedSpans[changedSpans.length - 1];
    const firstSpanText = firstSpan.node.text ?? '';
    const lastSpanText = lastSpan.node.text ?? '';
    const firstSpanPrefixEnd = clamp(start - firstSpan.start, 0, firstSpanText.length);
    const lastSpanSuffixStart = clamp(previousEnd - lastSpan.start, 0, lastSpanText.length);

    firstSpan.node.text = `${firstSpanText.slice(0, firstSpanPrefixEnd)}${replacementText}${lastSpanText.slice(
      lastSpanSuffixStart
    )}`;

    const remainingChangedSpans = changedSpans.slice(1);
    for (const changedSpan of remainingChangedSpans) {
      changedSpan.node.text = '';
    }

    return JSON.stringify(parsed);
  } catch {
    return toPlainTextLexicalJson(nextPlainText);
  }
};
